import { Add, CheckCircle, Close, Refresh, Work } from '@mui/icons-material';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EAnalysisStatus, type IRoleSelectionUIData } from '@sharedInterfaces/IEmployeeTalkState';
import type { IRoleSelectionComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type { AppState } from '@store/store';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

const ETTRoleSelectionComponent: React.FC<ITemplateComponentProps> = (props) => {
    const theme = useTheme();
    const allRoles = useSelector((state: AppState) => state.company.allRoles);
    const properties = props.component.properties as IRoleSelectionComponentProperties;

    const { secureState, selectRole, deselectRole, regenerateRoles, isReadOnlyForCurrentUser, isManualMode } = useEmployeeTalk();

    const cvAnalysis = {
        state: {
            isAnalyzing: secureState?.analysis.roles.status === EAnalysisStatus.LOADING,
            results: {
                extractedRoles: secureState?.analysis.roles.data || [],
            },
            error:
                secureState?.analysis.roles.status === EAnalysisStatus.ERROR ? 'CV-Analyse fehlgeschlagen' : undefined,
        },
    };

    const [acceptedItems, setAcceptedItems] = useState<{
        roles: Array<{ title: string; selected: boolean; id?: number }>;
    }>({ roles: [] });

    const [expandedForm, setExpandedForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [inlineForm, setInlineForm] = useState({ name: '' });
    const [isRegenerating, setIsRegenerating] = useState(false);

    useEffect(() => {
        const componentData = props.value as IRoleSelectionUIData | undefined;
        if (componentData?.roles) {
            setAcceptedItems({ roles: componentData.roles });
        }
    }, [props.value]);

    const handleAcceptRole = async (roleTitle: string) => {
        const previousState = acceptedItems.roles;
        setAcceptedItems((prev) => ({
            roles: prev.roles.map((r) => (r.title === roleTitle ? { ...r, selected: true } : r)),
        }));

        try {
            await selectRole(roleTitle);
        } catch (error) {
            setAcceptedItems({ roles: previousState });
            console.error('Failed to accept role:', error);
        }
    };

    const handleDeselectRole = async (roleTitle: string) => {
        const previousState = acceptedItems.roles;
        setAcceptedItems((prev) => ({
            roles: prev.roles.map((r) => (r.title === roleTitle ? { ...r, selected: false } : r)),
        }));

        try {
            await deselectRole(roleTitle);
        } catch (error) {
            setAcceptedItems({ roles: previousState });
            console.error('Failed to deselect role:', error);
        }
    };

    const handleAddRole = async () => {
        if (!inlineForm.name.trim()) {
            setError('Bitte wählen Sie eine Rolle aus');
            return;
        }
        setLoading(true);

        try {
            await selectRole(inlineForm.name);
            setInlineForm({ name: '' });
            setExpandedForm(false);
        } catch (error) {
            setError('Fehler beim Hinzufügen der Rolle');
        }
        setLoading(false);
    };

    const renderAnalysisResults = () => {
        const { results } = cvAnalysis.state;
        if (!results) return null;

        const suggestedRoleTitles = new Set((results.extractedRoles || []).map((r) => r.title));
        const manuallyAddedRoles = acceptedItems.roles.filter((role) => !suggestedRoleTitles.has(role.title));

        const availableRoles = allRoles.filter(
            (role) => !suggestedRoleTitles.has(role.title) && !manuallyAddedRoles.some((m) => m.title === role.title),
        );

        return (
            <Card sx={{ mb: 3 }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                <Work fontSize="small" />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 400 }}>
                                Erkannte Rollen
                            </Typography>
                        </Box>
                    }
                    sx={{ borderBottom: `1px solid ${theme.palette.divider}`, py: 2 }}
                />
                <CardContent>
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            border: `1px solid ${theme.palette.info.main}`,
                        }}
                    >
                        <Typography variant="body1" sx={{ fontSize: '0.9rem', color: theme.palette.info.main }}>
                            Wähle Rollen aus, die du ausgeübt hast. Du kannst weitere Rollen hinzufügen.
                        </Typography>
                    </Alert>

                    {results.extractedRoles && results.extractedRoles.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ color: 'text.primary', fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}
                            >
                                Aus CV extrahiert
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Rolle
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aktion
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.extractedRoles.map((role) => {
                                            const acceptedRole = acceptedItems.roles.find(
                                                (r) => r.title === role.title,
                                            );
                                            const isAccepted = acceptedRole?.selected === true;
                                            return (
                                                <TableRow
                                                    key={role.title}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">{role.title}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                                        >
                                                            {!isAccepted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        color: '#0BB140',
                                                                        borderColor: '#0BB140',
                                                                        '&:hover': {
                                                                            backgroundColor: '#0BB140',
                                                                            color: 'white',
                                                                        },
                                                                    }}
                                                                    onClick={() => handleAcceptRole(role.title)}
                                                                >
                                                                    ✓
                                                                </Button>
                                                            )}
                                                            {isAccepted && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="✓"
                                                                    size="small"
                                                                    onClick={
                                                                        isReadOnlyForCurrentUser
                                                                            ? undefined
                                                                            : () => handleDeselectRole(role.title)
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: '#0BB140',
                                                                        color: 'white',
                                                                        cursor: isReadOnlyForCurrentUser
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        '&:hover': isReadOnlyForCurrentUser
                                                                            ? {}
                                                                            : { backgroundColor: '#0a9e38' },
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {manuallyAddedRoles.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ color: 'text.primary', fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}
                            >
                                Manuell hinzugefügt
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Rolle
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.secondary.main,
                                                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderBottom: `2px solid ${theme.palette.secondary.main}`,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Aktion
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {manuallyAddedRoles.map((role) => {
                                            const isAccepted = role.selected === true;
                                            return (
                                                <TableRow
                                                    key={role.title}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">{role.title}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}
                                                        >
                                                            {!isAccepted && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isReadOnlyForCurrentUser}
                                                                    sx={{
                                                                        color: '#0BB140',
                                                                        borderColor: '#0BB140',
                                                                        '&:hover': {
                                                                            backgroundColor: '#0BB140',
                                                                            color: 'white',
                                                                        },
                                                                    }}
                                                                    onClick={() => handleAcceptRole(role.title)}
                                                                >
                                                                    ✓
                                                                </Button>
                                                            )}
                                                            {isAccepted && (
                                                                <Chip
                                                                    icon={<CheckCircle />}
                                                                    label="✓"
                                                                    size="small"
                                                                    onClick={
                                                                        isReadOnlyForCurrentUser
                                                                            ? undefined
                                                                            : () => handleDeselectRole(role.title)
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: '#0BB140',
                                                                        color: 'white',
                                                                        cursor: isReadOnlyForCurrentUser
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        '&:hover': isReadOnlyForCurrentUser
                                                                            ? {}
                                                                            : { backgroundColor: '#0a9e38' },
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {expandedForm && (
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Neue Rolle hinzufügen
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    freeSolo
                                    options={availableRoles.map((role) => role.title)}
                                    value={inlineForm.name}
                                    onChange={(_, newValue) => {
                                        setInlineForm({ name: newValue || '' });
                                        setError('');
                                    }}
                                    onInputChange={(_, newInputValue) => {
                                        setInlineForm({ name: newInputValue });
                                        setError('');
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Rolle auswählen oder eingeben"
                                            size="small"
                                            error={!!error}
                                            helperText={error}
                                        />
                                    )}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleAddRole}
                                        disabled={!inlineForm.name || loading}
                                        startIcon={loading ? <CircularProgress size={16} /> : null}
                                    >
                                        {loading ? 'Hinzufügen...' : 'Hinzufügen'}
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={() => setExpandedForm(false)}>
                                        Abbrechen
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {!isReadOnlyForCurrentUser && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            {!isManualMode && (secureState?.analysis.roles.status === EAnalysisStatus.COMPLETED || secureState?.analysis.roles.status === EAnalysisStatus.ERROR) && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                        isRegenerating || cvAnalysis.state.isAnalyzing ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <Refresh />
                                        )
                                    }
                                    onClick={async () => {
                                        setIsRegenerating(true);
                                        try {
                                            await regenerateRoles();
                                        } catch (error) {
                                            console.error('Failed to regenerate roles:', error);
                                        } finally {
                                            setTimeout(() => setIsRegenerating(false), 5000);
                                        }
                                    }}
                                    disabled={cvAnalysis.state.isAnalyzing || isRegenerating}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        borderColor: theme.palette.primary.main,
                                        '&:hover': { backgroundColor: theme.palette.primary.main, color: 'white' },
                                    }}
                                >
                                    {isRegenerating || cvAnalysis.state.isAnalyzing
                                        ? 'Generiere...'
                                        : 'Rollen neu analysieren'}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={expandedForm ? <Close /> : <Add />}
                                onClick={() => setExpandedForm(!expandedForm)}
                                sx={{
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                    '&:hover': { backgroundColor: theme.palette.primary.main, color: 'white' },
                                }}
                            >
                                {expandedForm ? 'Schließen' : 'Hinzufügen'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderEditContent = () => (
        <>
            <RowElement title="Label und Beschreibung ausblenden">
                <input
                    type="checkbox"
                    checked={properties.hideLabels ?? false}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                hideLabels: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
        </>
    );

    const renderViewContent = () => {
        const { state } = cvAnalysis;

        if (state.isAnalyzing) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Work sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Analysiere Rollen...
                    </Typography>
                    <LinearProgress sx={{ maxWidth: 300, mx: 'auto' }} />
                </Box>
            );
        }

        if (state.error) {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography variant="body1">{state.error}</Typography>
                    </Alert>
                </Box>
            );
        }

        return <Box sx={{ width: '100%' }}>{renderAnalysisResults()}</Box>;
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={renderEditContent}
            renderViewContent={renderViewContent}
            hideLabels={properties.hideLabels}
        />
    );
};

export default ETTRoleSelectionComponent;
